import React, { ReactElement } from "react";

import { ConnectedProps, connect } from "react-redux";

import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Grow from "@mui/material/Grow";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";

import { RootState } from "App/Store";

import { UserIcon } from "Components/Icons";
import Popper from "Components/Popper";

import { logUserOut } from "Adapters/profileAdapter";

import { profileSelector } from "Features/Profile/ProfileSlice";

const mapStateToProps = (state: RootState) => ({
  profile: profileSelector(state),
});

const connector = connect(mapStateToProps);
type TypesFromRedux = ConnectedProps<typeof connector>;

export function MainMenu(props: TypesFromRedux): ReactElement {
  const { profile } = props;
  const [open, setOpen] = React.useState<boolean>(false);
  const anchorRef = React.useRef<HTMLButtonElement | null>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: any) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current != null && !open) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);

  async function onSignOutClick() {
    try {
      await logUserOut();
      window.location.href = "/app/signed-out";
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <Box sx={{ display: "flex", zIndex: 1000 }}>
      <div>
        <IconButton
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-label="Open Main Menu"
          aria-haspopup="true"
          onClick={handleToggle}
          ref={anchorRef}
          tabIndex={1}
          size="large"
          sx={{
            color: "#FFF",
            "&:hover": {
              color: "secondary.main",
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="bottom-end"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList autoFocusItem={open} onKeyDown={handleListKeyDown}>
                    <ListItem>
                      <ListItemIcon>
                        <UserIcon />
                      </ListItemIcon>
                      <ListItemText>{profile?.displayName}</ListItemText>
                    </ListItem>
                    <Divider />
                    <MenuItem onClick={onSignOutClick}>Sign out</MenuItem>
                    <MenuItem component={Link} href="https://www.informx.com/?appId=releasenotes" target="_blank">
                      Release Notes
                    </MenuItem>
                    <MenuItem component={Link} href="https://blog.docutech.com/" id="btnMainMenuBlog" target="_blank">
                      Blog
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </Box>
  );
}

export default connector(MainMenu);
